/*eslint no-undef: "off"*/
import React from 'react';
import {Helmet} from 'react-helmet';

import MojitoCore from 'mojito/core';

import {absoluteUrlToInternalResource, stripRoutingPrefix} from '#core/utils/url-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {Localization} from '#core/localization.js';

import documentHeadDefaultConfig from 'mojito/core/generated/configs/_document-head.js';

import {actions as metaDataActions} from './store/slice.js';

const {dispatch} = MojitoCore.Services.redux.store;

MojitoCore.Services.Config.configRegistry.add(documentHeadDefaultConfig);

export default class DocumentHeadView extends MojitoCore.Presentation.UIViewImplementation {
    /**
     * Converting the locale from language-REGION (BCP 47) to language_TERRITORY, which is used by the Open Graph protocol:
     *   - https://ogp.me/
     *   - https://developers.facebook.com/docs/sharing/webmasters/
     *   - https://developers.facebook.com/docs/javascript/internationalization
     * @param {string} locale - e.g. en-US
     * @returns {string} - e.g. en_US
     */
    static toOpenGraphLocale(locale) {
        // There are two exceptions that do not follow the ISO standard: ar_AR and es_LA.
        switch (locale) {
            case 'es-ES':
                return 'es_LA';
            default:
                return locale.replace('-', '_');
        }
    }

    /**
     * Web page metadata that will be used:
     *   - by Helmet in the standalone app
     *   - by Portal through a subscription to PAGE_METATAGS_UPDATED
     * @typedef {object} MetaData
     * @property {string} title - Title
     * @property {string} [description] - Description
     * @property {string} [keywords] - Keywords
     * @property {string} [robots] - Robots meta tag (<meta name="robots" />)
     * @property {string} canonical - The canonical URL
     * @property {string} relativeCanonical - Relative representation of the current Sportsbook's URL
     * @property {Array[]} meta - List of meta tags
     */
    /**
     * Generate web page metadata
     * @param {object} metaFactory - Implementation of AbstractHeadMetaFactory
     * @param {object} data - Page information, see Mojito.Presentation.Components.DocumentHead.PageData
     * @returns {MetaData}
     */
    getMetaData(metaFactory, data) {
        if (metaFactory.setParameters) {
            // This factory is derived from DBX BaseMetaFactory
            // temp workaround for MOJ-11146
            metaFactory.setParameters({
                data,
                routeResolver: this.appContext().routeResolver,
            });
        }

        const factoryTitle = data && data.title && metaFactory.getTitle(data);
        const title = factoryTitle || this.resolveString('$DOCUMENT_HEAD.PAGE_TITLE');

        const meta = [];

        const factoryDescription = data && data.description && metaFactory.getDescription(data);
        const description = factoryDescription || this.resolveString('$DOCUMENT_HEAD.PAGE_DESCRIPTION');
        description && meta.push({name: 'description', content: description});

        const factoryKeywords = data && data.keywords && metaFactory.getKeywords(data);
        const keywords = factoryKeywords || this.resolveString('$DOCUMENT_HEAD.PAGE_KEYWORDS');
        keywords && meta.push({name: 'keywords', content: keywords});

        const canonical = metaFactory.getCanonical(data);
        const relativeCanonical =
            (data && metaFactory.getRelativeCanonical && metaFactory.getRelativeCanonical()) ||
            stripRoutingPrefix(ApplicationConfig.initParams.routingPrefix, window.location.pathname);

        let robots = '';
        metaFactory.getMeta &&
            metaFactory.getMeta().forEach(listItem => {
                for (const [key, value] of Object.entries(listItem)) {
                    meta.push({name: key, content: value});

                    if (key === 'robots' && robots.length === 0) {
                        robots = value;
                    }
                }
            });

        const openGraphImages = metaFactory.getOpenGraphImages ? metaFactory.getOpenGraphImages() : [];
        if (title && openGraphImages.length > 0) {
            const currentLocale = Localization.getFullCurrentLanguage();
            const availableLocales = OPERATIONAL_LANGUAGES;
            const og = [
                {property: 'og:title', content: title},
                {property: 'og:type', content: 'website'},
                {property: 'og:locale', content: DocumentHeadView.toOpenGraphLocale(currentLocale)},
                ...availableLocales
                    .filter(locale => locale !== currentLocale)
                    .map(locale => ({
                        property: 'og:locale:alternate',
                        content: DocumentHeadView.toOpenGraphLocale(locale),
                    })),
            ];
            openGraphImages.forEach(({url, width, height}) => {
                if (!url) return;
                og.push({property: 'og:image:url', content: absoluteUrlToInternalResource(url)});
                width && og.push({property: 'og:image:width', content: width});
                height && og.push({property: 'og:image:height', content: height});
            });
            meta.push(...og);
        }

        return {
            title,
            description,
            keywords,
            robots,
            canonical,
            relativeCanonical,
            meta,
        };
    }

    componentDidMount() {
        if (ApplicationConfig.isEmbedded) {
            const {metaFactory, data} = this.props;
            const metaData = this.getMetaData(metaFactory, data);

            dispatch(metaDataActions.setMetaData(metaData));
        }
    }

    componentDidUpdate(prevProps) {
        if (ApplicationConfig.isEmbedded) {
            const prevMetaData = this.getMetaData(prevProps.metaFactory, prevProps.data);
            const metaData = this.getMetaData(this.props.metaFactory, this.props.data);

            if (
                prevMetaData.title !== metaData.title ||
                prevMetaData.description !== metaData.description ||
                prevMetaData.keywords !== metaData.keywords
            ) {
                dispatch(metaDataActions.setMetaData(metaData));
            }
        }
    }

    render() {
        if (!this.config.enable || ApplicationConfig.isEmbedded) {
            return null;
        }

        const {metaFactory, data} = this.props;
        const {title, canonical, meta} = this.getMetaData(metaFactory, data);
        const links = [{rel: 'canonical', href: canonical}];

        return <Helmet meta={meta} link={links} title={title} />;
    }
}
